import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import styled from 'styled-components';

import { getCompanyActionPlanStats } from 'apis/ActionPlansAPI';
import { getCompanyAICICommunityRankings } from 'apis/RecommendationsAPI';
import CompanyAICIPageContainer from 'components/AiCompetitiveIntelligence/CompanyAICIPageContainer';
import useRecommendationsContext from 'components/AiCompetitiveIntelligence/RecommendationsContext';
import RecommendationsNotFound from 'components/AiCompetitiveIntelligence/RecommendationsNotFound';
import CommunitiesToWatchCard from 'components/CmiDigestOverview/CommunitiesToWatchCard';
import ComingSoonFeatureCards from 'components/ComingSoonFeatures/ComingSoonFeatureCards';
import SelectAdvancedFilter from 'components/lib/SelectAdvancedFilter';
import { canAccessActionPlansCompanyView } from 'helpers/permissions';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { handleSetSelectedCommunity, handleSetSelectedParentCommunity } from 'store/slices/globalSlice';

import ActionPlansPortfolioCard from './ActionPlansPortfolioCard';
import AIOverviewHeader from './AIOverviewHeader';
import CommunityRankingsCard from './CommunityRankingsCard';
import MyCommunitiesSection from './MyCommunities/MyCommunitiesSection';

const Container = styled.div`
  width: 100%;
`;

const FiltersSection = styled.div`
  margin-bottom: 20px;
`;

const CompanyAIOverview = () => {
  const dispatch = useAppDispatch();
  const { currentUser } = useAppSelector((state) => state.auth);
  const { communities, selectedCommunity } = useAppSelector((state) => state.global);
  const { company: selectedCompany } = useRecommendationsContext();
  const [selectedCommunityIds, setSelectedCommunityIds] = useState<number[]>([]);

  const communityOptions = useMemo(
    () =>
      (communities ?? [])
        .filter((community) => community.company_id === selectedCompany?.id)
        .map((community) => ({ value: community.id, label: community.comunity_name })),
    [communities, selectedCompany?.id]
  );

  useEffect(() => {
    setSelectedCommunityIds([]);
  }, [selectedCompany?.id]);

  useEffect(() => {
    if (selectedCommunity) {
      dispatch(handleSetSelectedCommunity(undefined));
      dispatch(handleSetSelectedParentCommunity(undefined));
    }
  }, [dispatch, selectedCommunity]);

  const { data, isLoading } = useQuery({
    queryKey: ['ai-overview-company-rankings', selectedCompany?.id],
    queryFn: () => getCompanyAICICommunityRankings(selectedCompany?.id ?? -1),
    enabled: Boolean(selectedCompany?.id),
    refetchOnWindowFocus: false,
  });

  const showActionPlansPortfolio = canAccessActionPlansCompanyView(currentUser);

  const { data: actionPlanStats, isLoading: isActionPlanStatsLoading } = useQuery({
    queryKey: ['ai-overview-company-action-plan-stats', selectedCompany?.id, selectedCommunityIds],
    queryFn: () => getCompanyActionPlanStats(selectedCompany?.id ?? -1, selectedCommunityIds),
    enabled: showActionPlansPortfolio && Boolean(selectedCompany?.id),
    refetchOnWindowFocus: false,
  });

  const allRows = data ?? [];
  const hasData = allRows.length > 0;

  const rows = useMemo(() => {
    if (selectedCommunityIds.length === 0) {
      return allRows;
    }
    return allRows.filter((row) => selectedCommunityIds.includes(row.community_id));
  }, [allRows, selectedCommunityIds]);

  const handleCommunitySelect = (selection: { value: number }[]) => {
    setSelectedCommunityIds(selection.map((option) => option.value));
  };

  return (
    <Container>
      <AIOverviewHeader />
      <CompanyAICIPageContainer>
        {!isLoading && (
          <FiltersSection>
            <SelectAdvancedFilter
              dataTestId="company-ai-overview-community-filter"
              placeholder="Community"
              options={communityOptions}
              defaultValue={selectedCommunityIds}
              onSelect={handleCommunitySelect}
            />
          </FiltersSection>
        )}
        {!isLoading && !hasData ? (
          <RecommendationsNotFound />
        ) : (
          <CommunityRankingsCard rows={rows} loading={isLoading} />
        )}
        <ComingSoonFeatureCards />
        <CommunitiesToWatchCard />
        {showActionPlansPortfolio && (
          <ActionPlansPortfolioCard stats={actionPlanStats} loading={isActionPlanStatsLoading} />
        )}
        <MyCommunitiesSection companyId={selectedCompany?.id} communityIds={selectedCommunityIds} />
      </CompanyAICIPageContainer>
    </Container>
  );
};

export default CompanyAIOverview;
